import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import { User, UserChat } from '@/slw/entities/chat';

import { useChatStore } from './chat-store';
import { db } from './config';

/**
 * Kiểm tra xem đã tồn tại cuộc trò chuyện giữa hai người dùng hay chưa
 * @param senderId ID của người gửi
 * @param receiverId ID của người nhận
 * @returns Thông tin về cuộc trò chuyện nếu tồn tại, null nếu không tồn tại
 */
export async function checkExistingChat(userId1: string, userId2: string, listingId?: string) {
  try {
    const userChatRef = doc(db, 'userchats', userId1);
    const userChatSnap = await getDoc(userChatRef);

    if (!userChatSnap.exists()) {
      return {
        exists: false,
        chatId: null,
        message: 'User chat document not found',
        receiverUser: null,
      };
    }

    const userData = userChatSnap.data();
    const userChats = userData.chats || [];

    const existingChat = userChats.find((chat: UserChat) => chat.receiverId === userId2);

    if (!existingChat) {
      return {
        exists: false,
        chatId: null,
        message: 'No existing chat found',
        receiverUser: null,
      };
    }

    const chatRef = doc(db, 'chats', existingChat.chatId);
    const chatSnap = await getDoc(chatRef);

    if (!chatSnap.exists()) {
      return {
        exists: false,
        chatId: null,
        message: 'Chat reference exists but chat document is missing',
        receiverUser: null,
      };
    }

    const receiverUserRef = doc(db, 'users', userId2);
    const receiverUserSnap = await getDoc(receiverUserRef);
    let receiverUser: User | null = null;

    if (receiverUserSnap.exists()) {
      receiverUser = {
        ...receiverUserSnap.data(),
      } as User;
    }

    let hasListingId = false;

    if (listingId) {
      hasListingId = await checkListingInMessages(existingChat.chatId, listingId);
    }

    return {
      exists: true,
      chatId: existingChat.chatId,
      message: 'Existing chat found and verified',
      chatData: existingChat,
      chatDocument: chatSnap.data(),
      receiverUser,
      hasListingId,
    };
  } catch (error) {
    return {
      exists: false,
      chatId: null,
      error: (error as Error).message,
      receiverUser: null,
    };
  }
}

export async function checkListingInMessages(chatId: string, listingId: string) {
  try {
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesRef, where('listingData.listingId', '==', listingId));
    const querySnapshot = await getDocs(q);

    return !querySnapshot.empty;
  } catch (error) {
    return false;
  }
}

/**
 * Mở cuộc trò chuyện hiện có hoặc tạo mới nếu chưa tồn tại
 * @param senderId ID của người gửi
 * @param receiverId ID của người nhận
 * @param receiverUser Thông tin người nhận
 * @returns Thông tin về cuộc trò chuyện
 */
export async function openExistingOrCreateChat(senderId: string, receiverId: string, receiverUser: any) {
  const existingChat = await checkExistingChat(senderId, receiverId);

  if (existingChat.exists && existingChat.chatId) {
    // Nếu cuộc trò chuyện đã tồn tại, mở nó
    const { openChat, changeChat } = useChatStore.getState();

    changeChat(existingChat.chatId, receiverUser);
    openChat();

    return {
      success: true,
      chatId: existingChat.chatId,
      isNew: false,
    };
  }

  // Nếu không tồn tại, trả về false để tạo mới
  return {
    success: false,
    isNew: true,
  };
}
